import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as DialogPrimitive from '@radix-ui/react-dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, Eye, EyeOff } from 'lucide-react';
import { useGateConfig } from '@/hooks/useGateConfig';
import { authService } from '@/lib/auth';
import { toast } from '@/hooks/use-toast';
import { z } from 'zod';
import { cn } from '@/lib/utils';
import { trackGateEvent } from '@/data/gateAnalytics';
import circleProLogo from '@/assets/circle-pro-logo.png';

interface ProductGateOverlayProps {
  open: boolean;
  productId: string | undefined;
  gateShownAt: number | null;
  onSignupSuccess: () => void;
}

const signupSchema = z.object({
  email: z.string().trim().email({ message: "Invalid email address" }),
  password: z.string().min(8, { message: "Password must be at least 8 characters" }),
  fullName: z.string().trim().min(2, { message: "Name must be at least 2 characters" }),
});

export function ProductGateOverlay({ open, productId, gateShownAt, onSignupSuccess }: ProductGateOverlayProps) {
  const navigate = useNavigate();
  const { data: config } = useGateConfig();
  
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  if (!config) return null;

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    // Validate input
    const result = signupSchema.safeParse({ email, password, fullName });
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach(err => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setLoading(true);
    try {
      const { error } = await authService.signUp(email, password, fullName);
      
      if (error) {
        if (error.message.includes('already registered')) {
          setErrors({ email: 'This email is already registered. Try logging in instead.' });
        } else {
          toast({
            variant: "destructive",
            title: "Signup failed",
            description: error.message,
          });
        }
        return;
      }

      // Success - track conversion
      const timeOnGate = gateShownAt ? Date.now() - gateShownAt : undefined;
      await trackGateEvent('signup', productId, undefined, timeOnGate);
      
      toast({
        title: "Account created!",
        description: "Welcome to Circle Network. Check your email to confirm your account.",
      });
      onSignupSuccess();
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Something went wrong",
        description: "Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleProClick = () => {
    const timeOnGate = gateShownAt ? Date.now() - gateShownAt : undefined;
    trackGateEvent('pro_click', productId, undefined, timeOnGate);
    navigate('/pricing?from=product_gate');
  };

  return (
    <DialogPrimitive.Root open={open}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0" />
        <DialogPrimitive.Content
          onInteractOutside={(e) => e.preventDefault()}
          onEscapeKeyDown={(e) => e.preventDefault()}
          className={cn(
            "fixed left-[50%] top-[50%] z-50 translate-x-[-50%] translate-y-[-50%]",
            "max-w-4xl max-h-[90vh] w-[calc(100%-2rem)] md:w-full overflow-y-auto p-0",
            "bg-background border border-border/50 rounded-lg shadow-lg",
            "data-[state=open]:animate-in data-[state=closed]:animate-out",
            "data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0",
            "data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95",
            "data-[state=closed]:slide-out-to-left-1/2 data-[state=closed]:slide-out-to-top-[48%]",
            "data-[state=open]:slide-in-from-left-1/2 data-[state=open]:slide-in-from-top-[48%]"
          )}
        >
          <div className="p-4 md:p-6">
            {/* Header - compact on mobile */}
            <div className="text-center mb-4 md:mb-5">
              <p className="text-xs md:text-sm text-muted-foreground mb-2 md:mb-3">
                {config.context_text}
              </p>
              <h2 className="text-xl md:text-2xl font-bold mb-1 md:mb-1.5">
                {config.header_text}
              </h2>
              <p className="text-sm md:text-sm text-muted-foreground">
                {config.subheader_text}
              </p>
            </div>

          {/* Mobile: Form first, Benefits below. Desktop: Two columns */}
          <div className="space-y-6 md:space-y-0 md:grid md:grid-cols-2 md:gap-6">
            {/* Free Signup - appears first on mobile */}
            <div className="space-y-2 md:space-y-3">
              <div>
                <h3 className="text-base md:text-lg font-semibold mb-1.5 md:mb-2">{config.free_title}</h3>
                <p className="text-xs md:text-xs text-muted-foreground mb-2 md:mb-2">Create a free account to:</p>
                <ul className="space-y-0.5 md:space-y-1 mb-2 md:mb-3">
                  {config.free_bullets.map((bullet, idx) => (
                    <li key={idx} className="flex items-start gap-1.5 md:gap-1.5 text-xs md:text-xs text-muted-foreground">
                      <Check className="h-3.5 w-3.5 md:h-4 md:w-4 text-primary flex-shrink-0 mt-0.5" />
                      <span>{bullet}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <form onSubmit={handleSignup} className="space-y-2 md:space-y-2.5">
                <div>
                  <Label htmlFor="fullName" className="text-xs md:text-xs">Full Name</Label>
                  <Input
                    id="fullName"
                    type="text"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="John Doe"
                    disabled={loading}
                    className={cn("h-9 md:h-10 text-sm", errors.fullName && 'border-destructive')}
                  />
                  {errors.fullName && (
                    <p className="text-xs text-destructive mt-0.5">{errors.fullName}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="email" className="text-xs md:text-xs">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@example.com"
                    disabled={loading}
                    className={cn("h-9 md:h-10 text-sm", errors.email && 'border-destructive')}
                  />
                  {errors.email && (
                    <p className="text-xs text-destructive mt-0.5">{errors.email}</p>
                  )}
                </div>

                <div>
                  <Label htmlFor="password" className="text-xs md:text-xs">Password</Label>
                  <div className="relative">
                    <Input
                      id="password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      placeholder="••••••••"
                      disabled={loading}
                      className={cn("h-9 md:h-10 text-sm", errors.password && 'border-destructive')}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword(!showPassword)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      tabIndex={-1}
                    >
                      {showPassword ? <EyeOff className="h-3.5 w-3.5 md:h-4 md:w-4" /> : <Eye className="h-3.5 w-3.5 md:h-4 md:w-4" />}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="text-xs text-destructive mt-0.5">{errors.password}</p>
                  )}
                </div>

                <Button
                  type="submit"
                  className="w-full h-9 md:h-10 text-sm md:text-sm mt-3 md:mt-3"
                  disabled={loading}
                >
                  {loading ? 'Creating account...' : 'Create Free Account'}
                </Button>
                <p className="text-[10px] md:text-[10px] text-center text-muted-foreground mt-1 md:mt-1.5">
                  {config.free_cta_subtext}
                </p>
              </form>
            </div>

            {/* Pro Benefits - appears second on mobile, shown below fold */}
            <div className="border-t md:border-t-0 pt-6 md:pt-0 md:border-l md:pl-6 space-y-2 md:space-y-3">
              <div>
                <div className="flex justify-center mb-3 md:mb-4">
                  <img src={circleProLogo} alt="Circle Pro" className="h-20 md:h-24 w-auto" />
                </div>
                <h3 className="text-base md:text-lg font-semibold mb-1 md:mb-1.5">{config.pro_title}</h3>
                <p className="text-xs md:text-xs text-muted-foreground mb-2 md:mb-3">
                  {config.pro_subheader}
                </p>

                {/* Save Money Section */}
                <div className="mb-2 md:mb-2.5">
                  <h4 className="text-xs md:text-sm font-semibold mb-0.5 md:mb-1 flex items-center gap-1">
                    <span>💰</span> Save Money
                  </h4>
                  <ul className="space-y-0.5 md:space-y-0.5">
                    {config.pro_save_money.map((bullet, idx) => (
                      <li key={idx} className="flex items-start gap-1 md:gap-1.5 text-xs md:text-xs text-muted-foreground">
                        <Check className="h-3 w-3 md:h-3.5 md:w-3.5 text-primary flex-shrink-0 mt-0.5" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Make Money Section */}
                <div className="mb-2 md:mb-2.5">
                  <h4 className="text-xs md:text-sm font-semibold mb-0.5 md:mb-1 flex items-center gap-1">
                    <span>💵</span> Make Money
                  </h4>
                  <ul className="space-y-0.5 md:space-y-0.5">
                    {config.pro_make_money.map((bullet, idx) => (
                      <li key={idx} className="flex items-start gap-1 md:gap-1.5 text-xs md:text-xs text-muted-foreground">
                        <Check className="h-3 w-3 md:h-3.5 md:w-3.5 text-primary flex-shrink-0 mt-0.5" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                {/* Save Time Section */}
                <div className="mb-3 md:mb-3">
                  <h4 className="text-xs md:text-sm font-semibold mb-0.5 md:mb-1 flex items-center gap-1">
                    <span>⏱️</span> Save Time
                  </h4>
                  <ul className="space-y-0.5 md:space-y-0.5">
                    {config.pro_save_time.map((bullet, idx) => (
                      <li key={idx} className="flex items-start gap-1 md:gap-1.5 text-xs md:text-xs text-muted-foreground">
                        <Check className="h-3 w-3 md:h-3.5 md:w-3.5 text-primary flex-shrink-0 mt-0.5" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>

              <Button
                type="button"
                variant="outline"
                className="w-full h-9 md:h-10 text-sm md:text-sm border-primary hover:bg-primary hover:text-primary-foreground"
                onClick={handleProClick}
              >
                {config.pro_cta_label}
              </Button>
              {config.pro_cta_subtext && (
                <p className="text-[10px] md:text-[10px] text-center text-muted-foreground">
                  {config.pro_cta_subtext}
                </p>
              )}
            </div>
          </div>

            {/* Footer - compact on mobile */}
            <div className="mt-4 md:mt-4 pt-4 md:pt-4 border-t border-border/50 text-center space-y-1 md:space-y-1">
              <div className="flex items-center justify-center gap-2 text-xs md:text-xs text-muted-foreground">
                <span className="text-lg md:text-lg">💡</span>
                <p>{config.quick_stat}</p>
              </div>
              <p className="text-[10px] md:text-[10px] text-muted-foreground">
                {config.footer_text}
              </p>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
